import { useMemo, useState } from "react";

export function Tile<T extends { thumb: string; category?: string }>({
  entry,
  onClick,
  stars,
  progress,
  onDelete,
}: {
  entry: T;
  onClick: () => void;
  /** Optional star rating */
  stars?: number;
  /** Optional fill progress (0..1) */
  progress?: number;
  onDelete?: () => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const isDataUrl = entry.thumb.startsWith("data:");
  // Clamp to [0,1] and compute %
  const progressPct = useMemo(() => {
    if (progress == null || Number.isNaN(progress)) return undefined;
    const clamped = Math.max(0, Math.min(1, progress));
    return Math.round(clamped * 100);
  }, [progress]);

  return (
    <button
      onClick={onClick}
      style={{
        all: "unset",
        cursor: "pointer",
        display: "block",
        width: "100%",
        position: "relative",
      }}
    >
      {/* Ratio box enforces square height */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 0,
          paddingBottom: "100%",
          borderRadius: 16,
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.06) inset, 0 10px 25px rgba(0,0,0,0.35)",
          transition:
            "transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease",
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.transform = "translateY(-2px)";
          el.style.boxShadow =
            "0 1px 0 rgba(255,255,255,0.06) inset, 0 18px 30px rgba(0,0,0,0.45)";
          el.style.borderColor = "rgba(255,255,255,0.22)";
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          el.style.transform = "translateY(0)";
          el.style.boxShadow =
            "0 1px 0 rgba(255,255,255,0.06) inset, 0 10px 25px rgba(0,0,0,0.35)";
          el.style.borderColor = "rgba(255,255,255,0.12)";
        }}
      >
        <img
          src={entry.thumb}
          loading={isDataUrl ? "eager" : "lazy"}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            opacity: loaded ? 1 : 0,
            transition: "opacity 200ms ease",
            filter: "saturate(1.05) contrast(1.02)",
          }}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          decoding="async"
        />

        {!loaded && (
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.12) 40%, rgba(255,255,255,0.06) 80%)",
              animation: "shimmer 1.2s infinite",
            }}
          />
        )}

        {/* Progress bar (no debug text) */}
        {typeof progressPct === "number" && (
          <div
            aria-label={`Progress ${progressPct}%`}
            role="progressbar"
            aria-valuenow={progressPct}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{
              position: "absolute",
              left: 12,
              right: 12,
              bottom: 8,
              height: 4,
              zIndex: 3,
              background: "rgba(255,204,0,0.3)",
              boxShadow: "0 0 4px rgba(255,255,255,0.9)", // white glow to stand out on dark photos
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                width: `${progressPct}%`,
                height: "100%",
                background: "linear-gradient(90deg, #ffcc00, #ff8800)", // bright gold/orange gradient
                boxShadow: "0 0 4px rgba(255,255,255,0.8)", // white glow to stand out on dark photos
                transition: "width 180ms ease",
              }}
            />
          </div>
        )}

        {/* Stars */}
        {stars !== undefined && (
          <div
            style={{
              position: "absolute",
              top: 8,
              left: 8,
              background: "rgba(0,0,0,0.7)",
              borderRadius: 8,
              padding: "2px 6px",
              color: "#FFD700",
              fontSize: 12,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              zIndex: 2,
              pointerEvents: "none",
            }}
          >
            {stars}★
          </div>
        )}

        {/* Delete button */}
        {onDelete && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Delete tile"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                onDelete();
              }
            }}
            style={{
              position: "absolute",
              top: 8,
              right: 8,
              background: "rgba(0,0,0,0.5)",
              borderRadius: "50%",
              padding: 4,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 3,
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6l-1 14H6L5 6" />
              <path d="M10 11v6" />
              <path d="M14 11v6" />
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            </svg>
          </div>
        )}
      </div>
    </button>
  );
}
